import { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  ScrollText,
  Sparkles,
  X,
  ChevronDown,
  ChevronRight,
  Pencil,
  Check,
} from 'lucide-react';
import { LoadingState, Spinner } from '@systemdox/ui';
import { Link } from 'react-router-dom';
import { rulesPromptsApi, type PromptTemplate } from '@/services/rulesPromptsApi';

export default function PromptsTab() {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  // Template form state
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templatePrompt, setTemplatePrompt] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Template expand/edit state
  const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(null);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<PromptTemplate | null>(null);

  useEffect(() => {
    rulesPromptsApi
      .listPromptTemplates()
      .then(setTemplates)
      .catch(() => setError('Failed to load templates.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleAddTemplate() {
    if (!templateName.trim() || !templatePrompt.trim()) return;
    setSavingTemplate(true);
    setError(null);
    try {
      const template = await rulesPromptsApi.addPromptTemplate({
        name: templateName.trim(),
        description: templateDescription.trim(),
        prompt: templatePrompt.trim(),
      });
      setTemplates((prev) => [template, ...prev]);
      setTemplateName('');
      setTemplateDescription('');
      setTemplatePrompt('');
      setShowTemplateForm(false);
    } catch {
      setError('Failed to add template. Please try again.');
    } finally {
      setSavingTemplate(false);
    }
  }

  async function handleDeleteTemplate(id: string) {
    setError(null);
    try {
      await rulesPromptsApi.deletePromptTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      if (editingTemplateId === id) {
        setEditingTemplateId(null);
        setEditDraft(null);
      }
    } catch {
      setError('Failed to delete template. Please try again.');
    }
  }

  function startEditingTemplate(template: PromptTemplate) {
    setEditingTemplateId(template.id);
    setEditDraft({ ...template });
  }

  function cancelEditingTemplate() {
    setEditingTemplateId(null);
    setEditDraft(null);
  }

  async function saveTemplateEdit() {
    if (!editDraft || !editingTemplateId) return;
    setSavingTemplate(true);
    setError(null);
    try {
      const updated = await rulesPromptsApi.updatePromptTemplate(editingTemplateId, {
        name: editDraft.name,
        description: editDraft.description,
        prompt: editDraft.prompt,
      });
      setTemplates((prev) => prev.map((t) => (t.id === editingTemplateId ? updated : t)));
      setEditingTemplateId(null);
      setEditDraft(null);
    } catch {
      setError('Failed to update template. Please try again.');
    } finally {
      setSavingTemplate(false);
    }
  }

  if (loading) {
    return <LoadingState size="lg" />;
  }

  return (
    <div className="space-y-12">
      {error && (
        <div className="flex items-center justify-between px-4 py-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <section>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              AI Prompt Templates
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Reusable prompt templates that Gen AI will use when generating content. Looking for
              project rules? They moved to{' '}
              <Link
                to="/docs/rules"
                className="font-medium text-teal-600 dark:text-teal-400 hover:underline inline-flex items-center gap-1"
              >
                <ScrollText className="w-3.5 h-3.5" /> Rules
              </Link>
              .
            </p>
          </div>
          <button
            onClick={() => setShowTemplateForm(!showTemplateForm)}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700"
          >
            <Plus className="w-4 h-4" /> Add Template
          </button>
        </div>

        {/* Add template form */}
        {showTemplateForm && (
          <div className="mb-6 p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Add Prompt Template
              </h3>
              <button
                onClick={() => setShowTemplateForm(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                  Name *
                </label>
                <input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g. Code Review, ADR Template, API Design"
                  className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                  Description
                </label>
                <input
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Brief description of when to use this prompt"
                  className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                  Prompt *
                </label>
                <textarea
                  value={templatePrompt}
                  onChange={(e) => setTemplatePrompt(e.target.value)}
                  rows={6}
                  placeholder="Enter the prompt template text..."
                  className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono resize-y"
                />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={handleAddTemplate}
                disabled={savingTemplate || !templateName.trim() || !templatePrompt.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
              >
                {savingTemplate && <Spinner size="sm" tone="current" />}
                Add
              </button>
            </div>
          </div>
        )}

        {/* Templates list */}
        {templates.length === 0 && !showTemplateForm ? (
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-4" />
            <p className="text-sm text-slate-500">No prompt templates yet.</p>
            <p className="text-xs text-slate-400 mt-1">
              Add reusable prompts for AI to use when generating content.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map((template) => {
              const isExpanded = expandedTemplateId === template.id;
              const isEditing = editingTemplateId === template.id;

              return (
                <div
                  key={template.id}
                  className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
                >
                  <button
                    onClick={() =>
                      setExpandedTemplateId(isExpanded ? null : template.id)
                    }
                    className="w-full flex items-center justify-between px-4 py-3 text-left"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {template.name}
                      </p>
                      {template.description && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {template.description}
                        </p>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="border-t border-slate-200 dark:border-slate-800 px-4 py-4 space-y-3">
                      {isEditing && editDraft ? (
                        <>
                          <div>
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                              Name
                            </label>
                            <input
                              value={editDraft.name}
                              onChange={(e) =>
                                setEditDraft({ ...editDraft, name: e.target.value })
                              }
                              className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                              Description
                            </label>
                            <input
                              value={editDraft.description}
                              onChange={(e) =>
                                setEditDraft({
                                  ...editDraft,
                                  description: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                              Prompt
                            </label>
                            <textarea
                              value={editDraft.prompt}
                              onChange={(e) =>
                                setEditDraft({
                                  ...editDraft,
                                  prompt: e.target.value,
                                })
                              }
                              rows={8}
                              className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono resize-y"
                            />
                          </div>
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={cancelEditingTemplate}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                              <X className="w-3.5 h-3.5" /> Cancel
                            </button>
                            <button
                              onClick={saveTemplateEdit}
                              disabled={savingTemplate}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                            >
                              {savingTemplate ? (
                                <Spinner size="sm" tone="current" />
                              ) : (
                                <Check className="w-3.5 h-3.5" />
                              )}
                              Save
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <pre className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-mono bg-slate-50 dark:bg-slate-800/50 rounded-md p-3 leading-relaxed">
                            {template.prompt}
                          </pre>
                          <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-100 dark:border-slate-800">
                            <button
                              onClick={() => startEditingTemplate(template)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button
                              onClick={() => handleDeleteTemplate(template.id)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Remove
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
